import React from "react";
import { useNavigate } from "react-router-dom";
import { ConvertBytesToImage } from "../../Utils/Utils";
import styled from "styled-components";

function ProductItem({ product }) {
    const navigate = useNavigate();

    const imageUrl = product ? ConvertBytesToImage(product?.image) : '';
    const hasMRP    = product?.mrp && product.mrp > product.price;
    const discountPct = hasMRP ? Math.round((1 - product.price / product.mrp) * 100) : 0;
    const outOfStock  = product?.stockQuantity === 0;
    const lowStock    = !outOfStock && product?.stockQuantity > 0 && product.stockQuantity < 10;

    const handleClick = (e) => {
        e.preventDefault();
        if (!outOfStock) navigate(`/productDetail/${product.id}`);
    };

    return (
        <StyledCard
            className={outOfStock ? "is-out-of-stock" : ""}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            aria-label={product?.name}
        >
            {/* ─── Image Zone ─── */}
            <div className="pc-img-zone">

                {/* Badges – top-left */}
                <div className="pc-badges-left">
                    {outOfStock && <span className="pc-badge badge-danger">Out of Stock</span>}
                    {!outOfStock && product?.requiresPrescription && (
                        <span className="pc-badge badge-warning">Rx</span>
                    )}
                </div>

                {/* Discount – top-right */}
                {hasMRP && !outOfStock && discountPct > 0 && (
                    <span className="pc-discount-pill">{discountPct}%<br />OFF</span>
                )}

                {/* Product image */}
                {imageUrl ? (
                    <img src={imageUrl} alt={product?.name} loading="lazy" className="pc-img" />
                ) : (
                    <div className="pc-img-fallback">
                        <i className="fa fa-capsules" />
                    </div>
                )}

                {/* Hover overlay — Add to Cart */}
                {!outOfStock && (
                    <div className="pc-overlay">
                        <button className="pc-cta-btn" onClick={handleClick}>
                            <i className="fa fa-shopping-cart" />
                            &nbsp; Add to Cart
                        </button>
                    </div>
                )}
            </div>

            {/* ─── Info Zone ─── */}
            <div className="pc-info">
                {product?.category && (
                    <span className="pc-category">{product.category}</span>
                )}

                <h3 className="pc-name">{product?.name}</h3>

                {/* Price row */}
                <div className="pc-price-row">
                    {outOfStock ? (
                        <span className="pc-unavailable">Unavailable</span>
                    ) : (
                        <>
                            <span className="pc-selling-price">₹{product?.price}</span>
                            {hasMRP && (
                                <span className="pc-original-price">
                                    <del>₹{product.mrp}</del>
                                </span>
                            )}
                            {hasMRP && discountPct > 0 && (
                                <span className="pc-savings">{discountPct}% off</span>
                            )}
                        </>
                    )}
                </div>

                {/* Stock alerts */}
                {lowStock && (
                    <p className="pc-stock-alert">
                        <i className="fa fa-clock-o" /> Only {product.stockQuantity} left!
                    </p>
                )}
            </div>
        </StyledCard>
    );
}

export default ProductItem;

/* ─────────────────────────────────────────
   Styled component
───────────────────────────────────────── */
const StyledCard = styled.div`
    /* Card shell */
    position: relative;
    background: #fff;
    border-radius: 14px;
    border: 1px solid #eef0f5;
    overflow: hidden;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    height: 100%;
    transition: transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1),
                box-shadow 0.28s ease,
                border-color 0.28s ease;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);

    &:hover:not(.is-out-of-stock) {
        transform: translateY(-6px);
        box-shadow: 0 16px 40px rgba(37, 99, 235, 0.12),
                    0 4px 12px rgba(0,0,0,0.08);
        border-color: #dbeafe;
    }

    &.is-out-of-stock {
        cursor: default;
        opacity: 0.65;
    }

    /* ── Image zone ── */
    .pc-img-zone {
        position: relative;
        height: 200px;
        background: #f8faff;
        overflow: hidden;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .pc-img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        padding: 12px;
        transition: transform 0.35s ease;
    }

    &:hover:not(.is-out-of-stock) .pc-img {
        transform: scale(1.07);
    }

    .pc-img-fallback {
        color: #c8d0e0;
        font-size: 3rem;
    }

    /* ── Badges ── */
    .pc-badges-left {
        position: absolute;
        top: 10px;
        left: 10px;
        z-index: 2;
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .pc-badge {
        font-size: 9px;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        padding: 3px 8px;
        border-radius: 20px;
        line-height: 1.5;
    }

    .badge-danger  { background: #fee2e2; color: #dc2626; }
    .badge-warning { background: #fef3c7; color: #b45309; }

    /* Discount pill – top right */
    .pc-discount-pill {
        position: absolute;
        top: 10px;
        right: 10px;
        z-index: 2;
        background: linear-gradient(135deg, #22c55e, #16a34a);
        color: #fff;
        font-size: 10px;
        font-weight: 800;
        line-height: 1.2;
        text-align: center;
        padding: 5px 7px;
        border-radius: 8px;
        letter-spacing: 0.03em;
    }

    /* ── Slide-up Add to Cart overlay ── */
    .pc-overlay {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        background: linear-gradient(to top, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0) 100%);
        padding: 20px 12px 10px;
        transform: translateY(100%);
        transition: transform 0.28s cubic-bezier(0.34, 1.2, 0.64, 1);
        z-index: 3;
    }

    &:hover:not(.is-out-of-stock) .pc-overlay {
        transform: translateY(0);
    }

    .pc-cta-btn {
        width: 100%;
        padding: 9px 0;
        background: linear-gradient(135deg, #2563eb, #1d4ed8);
        color: #fff;
        border: none;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 600;
        font-family: "Poppins", sans-serif;
        letter-spacing: 0.02em;
        cursor: pointer;
        transition: background 0.2s ease, transform 0.15s ease;
        box-shadow: 0 3px 10px rgba(37,99,235,0.35);
    }

    .pc-cta-btn:hover {
        background: linear-gradient(135deg, #1d4ed8, #1e40af);
        transform: scale(1.02);
    }

    /* ── Info zone ── */
    .pc-info {
        padding: 14px 14px 16px;
        display: flex;
        flex-direction: column;
        flex: 1;
        gap: 4px;
    }

    .pc-category {
        font-size: 10.5px;
        font-weight: 500;
        color: #2563eb;
        text-transform: uppercase;
        letter-spacing: 0.07em;
        background: #eff6ff;
        padding: 2px 8px;
        border-radius: 20px;
        align-self: flex-start;
        line-height: 1.6;
    }

    .pc-name {
        font-size: 14px;
        font-weight: 600;
        color: #1e293b;
        margin: 4px 0 0;
        line-height: 1.45;
        font-family: "Poppins", sans-serif;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    /* ── Price row ── */
    .pc-price-row {
        display: flex;
        align-items: baseline;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: auto;
        padding-top: 8px;
    }

    .pc-selling-price {
        font-size: 18px;
        font-weight: 700;
        color: #1e293b;
        font-family: "Poppins", sans-serif;
        letter-spacing: -0.01em;
    }

    .pc-original-price {
        font-size: 12.5px;
        color: #94a3b8;
        font-weight: 400;
    }

    .pc-savings {
        font-size: 12px;
        color: #16a34a;
        font-weight: 600;
        background: #f0fdf4;
        padding: 1px 7px;
        border-radius: 20px;
        align-self: center;
    }

    .pc-unavailable {
        font-size: 13px;
        color: #dc2626;
        font-weight: 500;
    }

    /* Stock alert */
    .pc-stock-alert {
        font-size: 11.5px;
        color: #ea580c;
        font-weight: 600;
        margin: 2px 0 0;
        display: flex;
        align-items: center;
        gap: 4px;
    }
`;
